import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Award, Bot, Crown, Handshake, Lightbulb, LoaderCircle, Map, TrendingUp, UserPlus, Users,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/adminRoles";
import { EASE } from "@/components/landing/Reveal";

function StatCard({ icon: Icon, label, value, sub, tone = "bleu", testid }) {
  const tones = {
    bleu: "bg-bleu-light text-bleu",
    amber: "bg-amber-100 text-amber-600",
    emerald: "bg-emerald-50 text-emeraude",
    rouge: "bg-red-50 text-rouge",
  };
  return (
    <div className="card-integra p-5" data-testid={testid}>
      <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${tones[tone]}`}><Icon className="h-4 w-4" /></span>
      <p className="mt-3 font-display text-2xl font-extrabold text-slate-900">{value}</p>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
      {sub && <p className="mt-1 text-[11px] text-slate-500">{sub}</p>}
    </div>
  );
}

export default function AdminOverview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const allowed = can(user, "stats");

  useEffect(() => {
    if (!allowed) return;
    api.get("/admin/stats").then((r) => setStats(r.data))
      .catch(() => toast.error("Impossible de charger les statistiques."));
  }, [allowed]);

  if (user && !allowed) return <Navigate to="/admin/moderation" replace />;
  if (!stats) return <div className="flex justify-center py-16"><LoaderCircle className="h-6 w-6 animate-spin text-slate-300" /></div>;

  const eur = (v) => v.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="page-admin-overview">
      <div>
        <h1 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Vue d'ensemble</h1>
        <p className="mt-1 text-sm text-slate-500">Statistiques globales de la plateforme INTEGRA FRANCE.</p>
      </div>

      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}
        className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard icon={Users} label="Utilisateurs" value={stats.users.total}
          sub={`${stats.users.by_role.user} usagers · ${stats.users.by_role.partner} partenaires`} testid="admin-stat-users" />
        <StatCard icon={UserPlus} label="Nouveaux (30 j)" value={stats.users.new_30d}
          sub={`${stats.users.new_7d} sur 7 jours`} tone="emerald" testid="admin-stat-new-users" />
        <StatCard icon={Crown} label="Comptes Premium" value={stats.users.premium}
          sub={`${stats.users.suspended} suspendu(s)`} tone="amber" testid="admin-stat-premium" />
        <StatCard icon={TrendingUp} label="Revenu mensuel estimé" value={eur(stats.revenue.total_mrr)}
          sub={`Premium ${eur(stats.revenue.premium_mrr)} · Partenaires ${eur(stats.revenue.partner_mrr)}`} tone="emerald" testid="admin-stat-revenue" />
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.06, ease: EASE }}
        className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard icon={Map} label="Progression moyenne" value={`${stats.progression.average} %`}
          sub="Parcours d'intégration" testid="admin-stat-progression" />
        <StatCard icon={Award} label="Examens blancs" value={stats.quiz.exams}
          sub={stats.quiz.success_rate != null ? `${stats.quiz.success_rate} % de réussite` : "Aucun examen terminé"} testid="admin-stat-quiz" />
        <StatCard icon={Bot} label="Messages IA" value={stats.ai.messages_total}
          sub={`${stats.ai.messages_30d} sur 30 jours`} testid="admin-stat-ai" />
        <StatCard icon={Lightbulb} label="Suggestions" value={stats.suggestions.total}
          sub={`${stats.suggestions.new} nouvelle(s)`} tone="amber" testid="admin-stat-suggestions" />
      </motion.div>

      <div className="grid gap-4 lg:grid-cols-2">
        <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1, ease: EASE }}
          className="card-integra p-6" data-testid="admin-signups-chart">
          <h2 className="text-sm font-bold text-slate-900">Inscriptions — 30 derniers jours</h2>
          {stats.users.signups_30d.length === 0 ? (
            <p className="mt-6 text-center text-xs text-slate-400">Aucune inscription sur la période.</p>
          ) : (
            <div className="mt-4 h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.users.signups_30d}>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={24} />
                  <Tooltip formatter={(v) => [v, "Inscriptions"]} labelFormatter={(d) => new Date(d).toLocaleDateString("fr-FR")} />
                  <Bar dataKey="count" fill="#1E3FAE" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.14, ease: EASE }}
          className="card-integra p-6" data-testid="admin-partners-summary">
          <h2 className="flex items-center gap-2 text-sm font-bold text-slate-900"><Handshake className="h-4 w-4 text-bleu" /> Partenaires</h2>
          <ul className="mt-4 space-y-2.5">
            {[
              { key: "pending_validation", label: "En attente de validation", cls: "bg-amber-100 text-amber-700" },
              { key: "pending_payment", label: "En attente de paiement", cls: "bg-slate-100 text-slate-600" },
              { key: "active", label: "Actifs", cls: "bg-emerald-100 text-emerald-700" },
              { key: "rejected", label: "Rejetés", cls: "bg-rose-100 text-rose-600" },
            ].map((row) => (
              <li key={row.key} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3">
                <span className="text-xs font-semibold text-slate-600">{row.label}</span>
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${row.cls}`} data-testid={`admin-partner-count-${row.key}`}>
                  {stats.partners[row.key] ?? 0}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[11px] text-slate-400">
            Quiz : {stats.quiz.completed} tentative(s) terminée(s) sur {stats.quiz.attempts} au total.
          </p>
        </motion.section>
      </div>
    </div>
  );
}
